/**
 * Ticket VS2: eine Aussendung – verfassen, Empfängerkreis, Testversand, Lauf.
 *
 * Der Empfängerkreis kommt aus derselben Auswahl wie die Vorschau der
 * Empfängerauswahl (`buildRecipientSelection`); hier steht keine zweite
 * Auswahl. Der Abmeldefuß entsteht im Renderpfad und ist in dieser Ansicht
 * nicht abschaltbar.
 */
import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { AdminPageHeader } from "@/components/admin/AdminPageHeader";
import { adminInputClass } from "@/components/admin/AdminField";
import { EmailMarkdownEditor } from "@/components/admin/EmailMarkdownEditor";
import { broadcastPreviewHtml } from "@/lib/broadcast-preview";
import { listBroadcastOrigins } from "@/lib/broadcast-origin.functions";
import { listContactSources } from "@/lib/contact-sources.functions";
import { listContactTags } from "@/lib/contacts.functions";
import { selectableSources } from "@/lib/contact-sources";
import { previewRecipients } from "@/lib/recipients.functions";
import {
  CONTACT_CLIENT_CATEGORY_LABELS,
  BUNDESLAENDER,
  BUNDESLAND_LABELS,
  BUNDESLAND_UNKNOWN,
  BUNDESLAND_UNKNOWN_LABEL,
  describeContactFilters,
  describeRecipientFilters,
  normalizeRecipientFilters,
  RECIPIENT_MAX_GROUPS,
  type ClientCategory,
  type ContactFilters,
} from "@/lib/contacts";
import {
  freezeBroadcastRecipients,
  getBroadcast,
  listBroadcastRecipients,
  previewBroadcastRender,
  removeBroadcastRecipient,
  resumeBroadcast,
  runBroadcast,
  saveBroadcast,
  sendBroadcastTest,
} from "@/lib/broadcasts.functions";
import {
  BROADCAST_PREVIEW_VARIANTS,
  BROADCAST_PREVIEW_VARIANT_LABELS,
  type BroadcastPreviewVariant,
} from "@/lib/broadcast-render";
import {
  BROADCAST_PLACEHOLDER_HINT,
  BROADCAST_PROCESSING_LABELS,
  RECIPIENT_VIEW_STATES,
  RECIPIENT_VIEW_STATE_LABELS,
  type RecipientViewState,
} from "@/lib/broadcasts";
import { StatusBadge } from "./_admin.admin.broadcasts.index";

export const Route = createFileRoute("/_admin/admin/broadcasts/$id")({
  component: BroadcastDetailPage,
  head: () => ({ meta: [{ title: "Aussendung – Interner Bereich" }] }),
});

const selectClass = adminInputClass + " pr-8";
const RECIPIENT_PAGE_SIZE = 50;

function BroadcastDetailPage() {
  const { id } = Route.useParams();
  const queryClient = useQueryClient();

  const getFn = useServerFn(getBroadcast);
  const saveFn = useServerFn(saveBroadcast);
  const freezeFn = useServerFn(freezeBroadcastRecipients);
  const runFn = useServerFn(runBroadcast);
  const resumeFn = useServerFn(resumeBroadcast);
  const testFn = useServerFn(sendBroadcastTest);
  const renderFn = useServerFn(previewBroadcastRender);
  const sourcesFn = useServerFn(listContactSources);
  const tagsFn = useServerFn(listContactTags);
  const originsFn = useServerFn(listBroadcastOrigins);
  const recipientsFn = useServerFn(previewRecipients);
  const listFn = useServerFn(listBroadcastRecipients);
  const removeFn = useServerFn(removeBroadcastRecipient);

  const detail = useQuery({
    queryKey: ["admin", "broadcast", id],
    queryFn: () => getFn({ data: { id } }),
    refetchInterval: (query) =>
      query.state.data?.processingState === "running" ||
      query.state.data?.processingState === "waiting_limit"
        ? 10_000
        : false,
  });
  const sourcesQuery = useQuery({
    queryKey: ["admin", "contact-sources"],
    queryFn: () => sourcesFn(),
    staleTime: 60_000,
  });
  const tagsQuery = useQuery({
    queryKey: ["admin", "contact-tags"],
    queryFn: () => tagsFn(),
    staleTime: 60_000,
  });
  const originsQuery = useQuery({
    queryKey: ["admin", "broadcast-origins"],
    queryFn: () => originsFn(),
    staleTime: 60_000,
  });

  const [subject, setSubject] = useState("");
  const [bodyMd, setBodyMd] = useState("");
  const [groups, setGroups] = useState<ContactFilters[]>([{}]);
  const [perRunLimit, setPerRunLimit] = useState(50);
  const [testEmail, setTestEmail] = useState("");
  const [loaded, setLoaded] = useState(false);
  // Ergebnis des letzten Laufs bleibt sichtbar – ein Toast allein verschwindet.
  const [lastRun, setLastRun] = useState<{
    enqueued: number;
    failed: number;
    remaining: number;
    blocked?: string;
  } | null>(null);
  const [recipientOffset, setRecipientOffset] = useState(0);
  // Empfängerliste des festgeschriebenen Kreises: Segment und Seite.
  const [listState, setListState] = useState<RecipientViewState | null>(null);
  const [listOffset, setListOffset] = useState(0);
  // Ticket VS2b: Vorschau und Testversand zeigen denselben Beispielzustand.
  const [previewVariant, setPreviewVariant] = useState<BroadcastPreviewVariant>("mit-namen");

  /** Filter ändern heißt: die Live-Liste beginnt wieder vorn. */
  const updateGroup = (index: number, fn: (prev: ContactFilters) => ContactFilters) => {
    setGroups((all) => all.map((g, i) => (i === index ? fn(g) : g)));
    setRecipientOffset(0);
  };
  const addGroup = () => {
    setGroups((all) => (all.length >= RECIPIENT_MAX_GROUPS ? all : [...all, {}]));
    setRecipientOffset(0);
  };
  const removeGroup = (index: number) => {
    setGroups((all) => (all.length <= 1 ? [{}] : all.filter((_, i) => i !== index)));
    setRecipientOffset(0);
  };

  // Erst laden, dann Formular belegen – sonst steht der Editor leer.
  useEffect(() => {
    if (!detail.data || loaded) return;
    setSubject(detail.data.subject);
    setBodyMd(detail.data.bodyMd);
    setGroups(normalizeRecipientFilters(detail.data.filters).groups);
    setPerRunLimit(detail.data.perRunLimit);
    setLoaded(true);
  }, [detail.data, loaded]);

  const frozen = Boolean(detail.data?.frozenAt);
  const metrics = detail.data?.metrics;

  const recipientPayload = useMemo(
    () =>
      normalizeRecipientFilters({
        groups: groups.map((g) => ({
          sourceId: g.sourceId ?? null,
          leadMagnet: g.leadMagnet ?? null,
          bundesland: g.bundesland ?? null,
          category: g.category ?? null,
          tag: g.tag ?? null,
        })),
      }),
    [groups],
  );

  // Live-Liste: Seitenzustand fällt bei jeder Filteränderung auf Anfang zurück.
  const recipientPreview = useQuery({
    queryKey: ["admin", "recipients", recipientPayload, recipientOffset],
    queryFn: () =>
      recipientsFn({
        data: { ...recipientPayload, offset: recipientOffset, limit: RECIPIENT_PAGE_SIZE },
      }),
    enabled: !frozen && loaded,
    staleTime: 30_000,
  });

  // Empfängerliste: Zustand aus Warteschlange und Versandspur zusammengeführt.
  const list = useQuery({
    queryKey: ["admin", "broadcast-recipients", id, listState, listOffset],
    queryFn: () =>
      listFn({
        data: { id, status: listState, offset: listOffset, limit: RECIPIENT_PAGE_SIZE },
      }),
    enabled: frozen,
    placeholderData: (prev) => prev,
    refetchInterval: detail.data?.processingState === "running" ? 10_000 : false,
  });

  const rendered = useQuery({
    queryKey: ["admin", "broadcast-render", subject, bodyMd, previewVariant],
    queryFn: () => renderFn({ data: { subject, bodyMd, variant: previewVariant } }),
    enabled: loaded,
  });

  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: ["admin", "broadcast", id] });
    await queryClient.invalidateQueries({ queryKey: ["admin", "broadcast-recipients", id] });
  };
  const fail = (err: unknown) =>
    toast.error(err instanceof Error ? err.message : "Vorgang fehlgeschlagen");

  const save = useMutation({
    mutationFn: () =>
      saveFn({ data: { id, subject, bodyMd, filters: recipientPayload, perRunLimit } }),
    onSuccess: async () => {
      toast.success("Gespeichert.");
      await refresh();
    },
    onError: fail,
  });

  const freeze = useMutation({
    mutationFn: () => freezeFn({ data: { id } }),
    onSuccess: async (result) => {
      toast.success(`Empfängerkreis festgeschrieben: ${result.total} Adressen.`);
      await refresh();
    },
    onError: fail,
  });

  const run = useMutation({
    mutationFn: () => runFn({ data: { id, limit: perRunLimit } }),
    onSuccess: async (result) => {
      setLastRun(result);
      const zahlen = `${result.enqueued} eingereiht, ${result.failed} gescheitert, ${result.remaining} noch einzureihen`;
      if (result.blocked) toast.error(`Aussendung angehalten (${zahlen}): ${result.blocked}`);
      else toast.success(`Aussendung verarbeitet: ${zahlen}.`);
      await refresh();
    },
    onError: fail,
  });

  const resume = useMutation({
    mutationFn: () => resumeFn({ data: { id } }),
    onSuccess: async (result) => {
      setLastRun(result);
      toast.success("Aussendung fortgesetzt – die Verarbeitung läuft automatisch weiter.");
      await refresh();
    },
    onError: fail,
  });

  const test = useMutation({
    mutationFn: () => testFn({ data: { id, email: testEmail.trim(), variant: previewVariant } }),
    onSuccess: () => toast.success("Testversand eingereiht."),
    onError: fail,
  });

  // Nur offene Adressen lassen sich nehmen; Angeschriebenes bleibt als Nachweis.
  const removeRecipient = useMutation({
    mutationFn: (recipientId: string) => removeFn({ data: { id, recipientId } }),
    onSuccess: async (result) => {
      toast.success(`Adresse entfernt. Empfängerkreis: ${result.total} Adressen.`);
      await refresh();
    },
    onError: fail,
  });

  const sources = selectableSources(sourcesQuery.data ?? []);
  const sourceNames = Object.fromEntries(sources.map((s) => [s.id, s.name]));

  if (detail.isPending) return <p className="text-sm text-muted-foreground">Wird geladen …</p>;
  if (detail.isError || !detail.data)
    return <p className="text-sm text-destructive">Die Aussendung ist nicht verfügbar.</p>;

  return (
    <div className="space-y-8">
      <AdminPageHeader
        title={detail.data.subject || "Aussendung"}
        description="Der Abmeldelink wird immer angehängt. Der Empfängerkreis wird beim ersten Lauf festgeschrieben."
      />

      <div className="flex flex-wrap items-center gap-3 text-sm">
        <StatusBadge status={detail.data.status} />
        <Link to="/admin/broadcasts" className="text-primary underline-offset-2 hover:underline">
          Zurück zur Übersicht
        </Link>
        {detail.data.pauseReason ? (
          <span className="text-destructive">{detail.data.pauseReason}</span>
        ) : null}
      </div>

      {/* Verfassen */}
      <section className="space-y-3">
        <h2 className="text-lg font-semibold">Nachricht</h2>
        <input
          aria-label="Betreff"
          className={adminInputClass}
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          disabled={frozen}
          maxLength={200}
        />
         <EmailMarkdownEditor id="broadcast-body" value={bodyMd} onChange={setBodyMd} disabled={frozen} rows={14} />
        <p className="text-xs text-muted-foreground">{BROADCAST_PLACEHOLDER_HINT}</p>
        {!frozen ? (
          <button
            type="button"
            onClick={() => save.mutate()}
            disabled={save.isPending}
            className="rounded-md bg-brand-button px-4 py-2 text-sm font-semibold text-brand-button-foreground disabled:opacity-60"
          >
            {save.isPending ? "Wird gespeichert …" : "Speichern"}
          </button>
        ) : (
          <p className="text-sm text-muted-foreground">
            Der Empfängerkreis ist festgeschrieben – Betreff und Text sind nicht mehr änderbar.
          </p>
        )}
      </section>

      {/* Vorschau */}
      <section className="space-y-3">
        <h2 className="text-lg font-semibold">Vorschau</h2>
        {/* Ticket VS2b: Der Leerfall ist umschaltbar – sonst sieht immer alles gut aus. */}
        <div className="flex flex-wrap items-center gap-2 text-sm">
          {BROADCAST_PREVIEW_VARIANTS.map((v) => (
            <button
              key={v}
              type="button"
              aria-pressed={previewVariant === v}
              onClick={() => setPreviewVariant(v)}
              className={
                "rounded-md border px-3 py-1.5 font-semibold " +
                (previewVariant === v
                  ? "border-primary bg-primary text-primary-foreground"
                  : "border-border text-muted-foreground hover:text-foreground")
              }
            >
              {BROADCAST_PREVIEW_VARIANT_LABELS[v]}
            </button>
          ))}
          <span className="text-xs text-muted-foreground">
            {previewVariant === "ohne-namen"
              ? `So kommt die Nachricht bei Kontakten ohne Vor- und Nachnamen an${
                  recipientPreview.data ? ` – ${recipientPreview.data.withoutName} im Kreis` : ""
                }. Testversand nutzt denselben Zustand.`
              : "Beispielwerte für Vorname, Nachname und Organisation. Testversand nutzt denselben Zustand."}
          </span>
        </div>
        <div className="rounded-lg border border-border bg-white">
          <iframe
            title="Vorschau der Aussendung"
            className="h-[520px] w-full rounded-lg"
            srcDoc={broadcastPreviewHtml(rendered.data?.html ?? "")}
          />
        </div>
        {/* Ticket VS2c: Nachrichtengröße vor dem Start – der Versanddienst
            nimmt höchstens 100 KB je Nachricht an. */}
        {rendered.data ? (
          <p
            className={
              "text-sm " +
              (rendered.data.tooLarge ? "font-semibold text-destructive" : "text-muted-foreground")
            }
          >
            {rendered.data.sizeNote}
          </p>
        ) : null}
      </section>

      {/* Empfängerkreis */}
      <section className="space-y-3">
        <h2 className="text-lg font-semibold">Empfängerkreis</h2>
        {frozen ? (
          <p className="text-sm text-muted-foreground">
            Festgeschrieben am {new Date(detail.data.frozenAt!).toLocaleString("de-DE")} ·{" "}
            {metrics?.total} Adressen.{" "}
            {describeRecipientFilters(detail.data.filters, { sourceNames })}
          </p>
        ) : (
          <>
            {groups.map((filters, index) => (
              <div key={index} className="space-y-2">
                {index > 0 && (
                  <p className="text-center text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    oder
                  </p>
                )}
                <fieldset aria-label={`Gruppe ${index + 1}`} className="space-y-2 rounded-[4px] border border-border bg-muted/30 p-3">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-sm font-semibold">
                      Gruppe {index + 1}
                      <span className="ml-2 font-normal text-muted-foreground">(alle Angaben müssen passen)</span>
                    </span>
                    {groups.length > 1 && (
                      <button
                        type="button"
                        className="text-sm text-muted-foreground underline hover:text-foreground"
                        onClick={() => removeGroup(index)}
                      >
                        Entfernen
                      </button>
                    )}
                  </div>
             <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
              <select
                aria-label="Nach Kategorie filtern"
                className={selectClass}
                value={filters.category ?? ""}
                onChange={(e) =>
                  updateGroup(index, (f) => ({
                    ...f,
                    category: (e.target.value || null) as ClientCategory | null,
                  }))
                }
              >
                <option value="">Alle Kategorien</option>
                {(Object.keys(CONTACT_CLIENT_CATEGORY_LABELS) as ClientCategory[]).map((k) => (
                  <option key={k} value={k}>
                    {CONTACT_CLIENT_CATEGORY_LABELS[k]}
                  </option>
                ))}
              </select>
              <select
                aria-label="Nach Herkunft filtern"
                className={selectClass}
                value={filters.sourceId ?? ""}
                onChange={(e) => updateGroup(index, (f) => ({ ...f, sourceId: e.target.value || null }))}
              >
                <option value="">Alle Herkünfte</option>
                {sources.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </select>
              <select
                aria-label="Nach Herkunftseintrag filtern"
                className={selectClass}
                value={filters.leadMagnet ?? ""}
                onChange={(e) => updateGroup(index, (f) => ({ ...f, leadMagnet: e.target.value || null }))}
              >
                <option value="">Alle Herkunftseinträge</option>
                {(originsQuery.data ?? []).map((label) => <option key={label} value={label}>{label}</option>)}
                {filters.leadMagnet && !originsQuery.data?.includes(filters.leadMagnet) && <option value={filters.leadMagnet}>{filters.leadMagnet} (bisherige Auswahl)</option>}
              </select>
              <select
                aria-label="Nach Bundesland filtern"
                className={selectClass}
                value={filters.bundesland ?? ""}
                onChange={(e) => updateGroup(index, (f) => ({ ...f, bundesland: (e.target.value || null) as ContactFilters["bundesland"] }))}
              >
                <option value="">Alle Bundesländer</option>
                {BUNDESLAENDER.map((land) => <option key={land} value={land}>{BUNDESLAND_LABELS[land]}</option>)}
                <option value={BUNDESLAND_UNKNOWN}>{BUNDESLAND_UNKNOWN_LABEL}</option>
              </select>
              <select
                aria-label="Nach Schlagwort filtern"
                className={selectClass}
                value={filters.tag ?? ""}
                onChange={(e) => updateGroup(index, (f) => ({ ...f, tag: e.target.value || null }))}
              >
                <option value="">Alle Schlagworte</option>
                {(tagsQuery.data ?? []).map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
            </div>
                </fieldset>
              </div>
            ))}
            {groups.length < RECIPIENT_MAX_GROUPS && (
              <button
                type="button"
                className="rounded-[4px] border border-dashed border-border px-3 py-2 text-sm hover:bg-muted"
                onClick={addGroup}
              >
                + Weitere Gruppe (oder)
              </button>
            )}
            <p className="text-sm text-muted-foreground">
               {recipientPreview.data && !recipientPreview.isFetching
                ? `${recipientPreview.data.total} Empfängerinnen und Empfänger · ${recipientPreview.data.overlap > 0 ? `davon ${recipientPreview.data.overlap} in mehreren Gruppen, je einmal gezählt · ` : ""}${describeRecipientFilters(recipientPayload, { sourceNames })}`
                : recipientPreview.isError
                  ? "Vorschau nicht möglich."
                  : "Vorschau wird geladen …"}
            </p>

            {/* Live-Liste: genau die Adressen, die beim Festschreiben in den Kreis wandern. */}
            <div className="rounded-lg border border-border">
              <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border px-4 py-3 text-sm">
                <span className="font-semibold">Ausgewählte Kontakte</span>
                 {recipientPreview.data && !recipientPreview.isFetching ? (
                  <span className="text-muted-foreground">
                    {recipientPreview.data.total === 0
                      ? "0 von 0"
                      : `${recipientPreview.data.offset + 1}–${recipientPreview.data.offset + recipientPreview.data.page.length} von ${recipientPreview.data.total}`}
                  </span>
                ) : null}
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-left text-sm">
                  <thead className="text-xs uppercase text-muted-foreground">
                    <tr className="border-b border-border">
                      <th scope="col" className="px-4 py-2 font-medium">
                        E-Mail
                      </th>
                      <th scope="col" className="px-4 py-2 font-medium">
                        Name
                      </th>
                      <th scope="col" className="px-4 py-2 font-medium">
                        Organisation
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                     {(!recipientPreview.isFetching ? recipientPreview.data?.page ?? [] : []).map((r) => (
                      <tr key={r.email}>
                        <td className="px-4 py-2 font-medium">{r.email}</td>
                        <td className="px-4 py-2 text-muted-foreground">{r.name || "—"}</td>
                        <td className="px-4 py-2 text-muted-foreground">{r.organization || "—"}</td>
                      </tr>
                    ))}
                     {recipientPreview.data && !recipientPreview.isFetching && recipientPreview.data.page.length === 0 ? (
                      <tr>
                        <td colSpan={3} className="px-4 py-3 text-muted-foreground">
                          Kein Kontakt erfüllt die Bedingungen.
                        </td>
                      </tr>
                    ) : null}
                    {/* Scheitert die Vorschau, steht der Grund hier – nicht endlos „Wird geladen“. */}
                     {recipientPreview.isError ? (
                      <tr>
                        <td colSpan={3} className="px-4 py-3">
                          <span className="text-destructive">
                            {recipientPreview.error instanceof Error
                              ? recipientPreview.error.message
                              : "Empfängerkreis nicht lesbar"}
                          </span>{" "}
                          <button
                            type="button"
                            onClick={() => void recipientPreview.refetch()}
                            className="font-semibold underline"
                          >
                            Erneut versuchen
                          </button>
                        </td>
                      </tr>
                    ) : null}
                     {recipientPreview.isFetching && !recipientPreview.isError ? (
                      <tr>
                        <td colSpan={3} className="px-4 py-3 text-muted-foreground">
                          Wird geladen …
                        </td>
                      </tr>
                    ) : null}
                  </tbody>
                </table>
              </div>
               {recipientPreview.data && !recipientPreview.isFetching && recipientPreview.data.total > RECIPIENT_PAGE_SIZE ? (
                <div className="flex items-center justify-between gap-2 border-t border-border px-4 py-2">
                  <button
                    type="button"
                    onClick={() => setRecipientOffset((o) => Math.max(0, o - RECIPIENT_PAGE_SIZE))}
                    disabled={recipientPreview.data.offset === 0}
                    className="rounded-md border border-border px-3 py-1.5 text-sm font-semibold disabled:opacity-50"
                  >
                    Zurück
                  </button>
                  <button
                    type="button"
                    onClick={() => setRecipientOffset((o) => o + RECIPIENT_PAGE_SIZE)}
                    disabled={
                      recipientPreview.data.offset + recipientPreview.data.page.length >=
                      recipientPreview.data.total
                    }
                    className="rounded-md border border-border px-3 py-1.5 text-sm font-semibold disabled:opacity-50"
                  >
                    Weiter
                  </button>
                </div>
              ) : null}
            </div>
            <button
              type="button"
              onClick={() => {
                if (
                  !confirm("Empfängerkreis jetzt festschreiben? Danach sind Text und Kreis fest.")
                )
                  return;
                save.mutate(undefined, { onSuccess: () => freeze.mutate() });
              }}
               disabled={freeze.isPending || save.isPending || recipientPreview.isFetching || recipientPreview.isError || !recipientPreview.data?.complete || originsQuery.isError}
              className="rounded-md border border-border px-4 py-2 text-sm font-semibold disabled:opacity-60"
            >
              Empfängerkreis festschreiben
            </button>
          </>
        )}
      </section>

      {/* Testversand */}
      <section className="space-y-3">
        <h2 className="text-lg font-semibold">Testversand</h2>
        <p className="text-sm text-muted-foreground">
          Nur an Adressen auf creategy.de. Der Testversand berührt weder den Empfängerkreis noch die
          Obergrenze eines Laufs.
        </p>
        <div className="flex flex-wrap gap-2">
          <input
            aria-label="Adresse für den Testversand"
            className={adminInputClass + " max-w-sm"}
            placeholder="name@creategy.de"
            value={testEmail}
            onChange={(e) => setTestEmail(e.target.value)}
          />
          <button
            type="button"
            onClick={() => test.mutate()}
            disabled={test.isPending || !testEmail.trim()}
            className="rounded-md border border-border px-4 py-2 text-sm font-semibold disabled:opacity-60"
          >
            {test.isPending ? "Wird eingereiht …" : "Testversand"}
          </button>
        </div>
      </section>

      {/* Aussendung */}
      <section className="space-y-3">
        <h2 className="text-lg font-semibold">Aussendung</h2>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
          <Kennzahl label="Empfängerkreis" value={metrics?.total ?? 0} strong />
          <Kennzahl label="zugestellt" value={detail.data.delivered} />
          <Kennzahl label="eingereiht" value={metrics?.enqueued ?? 0} />
          <Kennzahl label="offen" value={metrics?.open ?? 0} />
          <Kennzahl label="harte Rückläufer" value={metrics?.bounced ?? 0} />
        </div>
        <p className="text-sm text-muted-foreground">
          Die gewählte Zahl offener Kontakte wird diesem Versandauftrag fest zugeordnet und
          vollständig eingereiht. Bei zwei Prozent harten Rückläufern hält sich die Aussendung
          selbst an; das Fortsetzen entscheidet ein Mensch.
        </p>
        <div className="rounded-lg border border-border bg-secondary/40 p-4 text-sm">
          <p className="font-semibold">
            {BROADCAST_PROCESSING_LABELS[detail.data.processingState]}
          </p>
          {detail.data.processingNote ? (
            <p className="mt-1 text-muted-foreground">{detail.data.processingNote}</p>
          ) : null}
          {detail.data.nextAttemptAt && detail.data.processingState === "waiting_limit" ? (
            <p className="mt-1 text-muted-foreground">
              Nächster automatischer Versuch:{" "}
              {new Date(detail.data.nextAttemptAt).toLocaleString("de-DE")}
            </p>
          ) : null}
          {detail.data.processingTargetCount !== null ? (
            <p className="mt-2 font-semibold">
              Gewählt {detail.data.processingTargetCount} · {detail.data.processingClaimedCount}{" "}
              eingereiht · {detail.data.delivered} zugestellt ·{" "}
              {detail.data.processingRemainingCount} noch einzureihen
            </p>
          ) : null}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <label className="flex items-center gap-2 text-sm font-semibold">
            Menge
            <input
              aria-label="Menge dieser Aussendung"
              className={adminInputClass + " w-24"}
              type="number"
              min={1}
              max={500}
              value={perRunLimit}
              onChange={(event) =>
                setPerRunLimit(Math.max(1, Math.min(500, Number(event.target.value) || 1)))
              }
              disabled={run.isPending || detail.data.status === "angehalten"}
            />
          </label>
          <button
            type="button"
            onClick={() => {
              const possible = Math.min(perRunLimit, metrics?.open ?? perRunLimit);
              if (
                !confirm(
                  `${possible} Adressen aussenden? Diese Kontakte werden jetzt fest für den Versand ausgewählt.`,
                )
              )
                return;
              run.mutate();
            }}
            disabled={
              run.isPending ||
              detail.data.status === "angehalten" ||
              Boolean(rendered.data?.tooLarge)
            }
            className="rounded-md bg-brand-button px-4 py-2 text-sm font-semibold text-brand-button-foreground disabled:opacity-60"
          >
            {run.isPending ? "Wird gestartet …" : "Aussendung starten"}
          </button>
          {detail.data.status === "angehalten" ? (
            <button
              type="button"
              onClick={() => resume.mutate()}
              disabled={resume.isPending}
              className="rounded-md border border-border px-4 py-2 text-sm font-semibold disabled:opacity-60"
            >
              Fortsetzen
            </button>
          ) : null}
        </div>

        {lastRun ? (
          <div
            className={
              "rounded-lg border p-4 text-sm " +
              (lastRun.blocked
                ? "border-destructive/30 bg-destructive/10"
                : "border-border bg-secondary/40")
            }
          >
            <p className="font-semibold">
              Letzte Verarbeitung: {lastRun.enqueued} eingereiht, {lastRun.remaining} noch
              einzureihen
              {lastRun.failed > 0 ? `, ${lastRun.failed} gescheitert` : ""}
            </p>
            {lastRun.blocked ? (
              <p className="mt-1 text-destructive">Angehalten weil: {lastRun.blocked}</p>
            ) : null}
          </div>
        ) : null}

        {detail.data.recentRuns.length > 0 ? (
          <div className="overflow-x-auto rounded-lg border border-border">
            <table className="w-full text-left text-sm">
              <thead className="text-xs uppercase text-muted-foreground">
                <tr className="border-b border-border">
                  <th className="px-4 py-2 font-semibold">Zeitpunkt</th>
                  <th className="px-4 py-2 font-semibold">Eingereiht</th>
                  <th className="px-4 py-2 font-semibold">Zugestellt gesamt</th>
                  <th className="px-4 py-2 font-semibold">Offen</th>
                  <th className="px-4 py-2 font-semibold">Hinweis</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {detail.data.recentRuns.map((entry) => (
                  <tr key={entry.id}>
                    <td className="px-4 py-2 text-muted-foreground">
                      {new Date(entry.startedAt).toLocaleString("de-DE")}
                    </td>
                    <td className="px-4 py-2">{entry.enqueued}</td>
                    <td className="px-4 py-2">{entry.delivered}</td>
                    <td className="px-4 py-2">{entry.remaining}</td>
                    <td className="px-4 py-2 text-muted-foreground">
                      {entry.reason ?? (entry.failed > 0 ? `${entry.failed} fehlgeschlagen` : "—")}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}

        {frozen ? (
          <div className="rounded-lg border border-border">
            <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border px-4 py-3 text-sm">
              <span className="font-semibold">Empfängerinnen und Empfänger</span>
              {list.data ? (
                <span className="text-muted-foreground">
                  {list.data.filtered === 0
                    ? "0 von 0"
                    : `${list.data.offset + 1}–${list.data.offset + list.data.rows.length} von ${list.data.filtered}`}
                </span>
              ) : null}
            </div>

            {/* Segmentierung nach Zustand. */}
            <div className="flex flex-wrap gap-2 border-b border-border px-4 py-3 text-sm">
              <button
                type="button"
                aria-pressed={listState === null}
                onClick={() => {
                  setListState(null);
                  setListOffset(0);
                }}
                className={
                  "rounded-md border px-3 py-1 font-semibold " +
                  (listState === null
                    ? "border-primary bg-primary text-primary-foreground"
                    : "border-border text-muted-foreground hover:text-foreground")
                }
              >
                Alle{list.data ? ` (${list.data.total})` : ""}
              </button>
              {RECIPIENT_VIEW_STATES.map((s) => (
                <button
                  key={s}
                  type="button"
                  aria-pressed={listState === s}
                  onClick={() => {
                    setListState(s);
                    setListOffset(0);
                  }}
                  className={
                    "rounded-md border px-3 py-1 font-semibold " +
                    (listState === s
                      ? "border-primary bg-primary text-primary-foreground"
                      : "border-border text-muted-foreground hover:text-foreground")
                  }
                >
                  {RECIPIENT_VIEW_STATE_LABELS[s]}
                  {list.data ? ` (${list.data.counts[s]})` : ""}
                </button>
              ))}
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead className="text-xs uppercase text-muted-foreground">
                  <tr className="border-b border-border">
                    <th className="px-4 py-2 font-semibold">E-Mail</th>
                    <th className="px-4 py-2 font-semibold">Name</th>
                    <th className="px-4 py-2 font-semibold">Organisation</th>
                    <th className="px-4 py-2 font-semibold">Zustand</th>
                    <th className="px-4 py-2 font-semibold">Zeitpunkt</th>
                    <th className="px-4 py-2 font-semibold text-right">Aktion</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {(list.data?.rows ?? []).map((r) => (
                    <tr key={r.id}>
                      <td className="px-4 py-2 font-medium">{r.email}</td>
                      <td className="px-4 py-2 text-muted-foreground">{r.name || "—"}</td>
                      <td className="px-4 py-2 text-muted-foreground">{r.organization || "—"}</td>
                      <td className="px-4 py-2">
                        <div className="flex flex-wrap items-center gap-2">
                          <ZustandBadge view={r.view} />
                          {r.view === "offen" && r.selectedForCurrentRun ? (
                            <span className="rounded-md bg-brand-button/20 px-2 py-0.5 text-xs font-semibold">
                              für diese Aussendung ausgewählt
                            </span>
                          ) : null}
                          {r.note ? (
                            <span className="text-xs text-muted-foreground">{r.note}</span>
                          ) : null}
                        </div>
                      </td>
                      <td className="px-4 py-2 text-muted-foreground">
                        {r.at ? new Date(r.at).toLocaleString("de-DE") : "—"}
                      </td>
                      <td className="px-4 py-2 text-right">
                        {r.view === "offen" ? (
                          <button
                            type="button"
                            onClick={() => {
                              if (
                                !confirm(
                                  `${r.email} aus dem Empfängerkreis entfernen? Diese Adresse wird in keinem Lauf mehr angeschrieben.`,
                                )
                              )
                                return;
                              removeRecipient.mutate(r.id);
                            }}
                            disabled={removeRecipient.isPending}
                            className="rounded-md border border-border px-2 py-1 text-xs font-semibold text-destructive disabled:opacity-50"
                          >
                            Entfernen
                          </button>
                        ) : (
                          <span className="text-xs text-muted-foreground">bleibt als Nachweis</span>
                        )}
                      </td>
                    </tr>
                  ))}
                  {list.data && list.data.rows.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="px-4 py-3 text-muted-foreground">
                        Keine Adresse in diesem Zustand.
                      </td>
                    </tr>
                  ) : null}
                  {!list.data ? (
                    <tr>
                      <td colSpan={6} className="px-4 py-3">
                        {list.isError ? (
                          <span className="text-destructive">
                            Die Empfängerliste ist nicht lesbar.
                          </span>
                        ) : (
                          <span className="text-muted-foreground">Wird geladen …</span>
                        )}
                      </td>
                    </tr>
                  ) : null}
                </tbody>
              </table>
            </div>

            {list.data && list.data.filtered > RECIPIENT_PAGE_SIZE ? (
              <div className="flex items-center justify-between gap-2 border-t border-border px-4 py-2">
                <button
                  type="button"
                  onClick={() => setListOffset((o) => Math.max(0, o - RECIPIENT_PAGE_SIZE))}
                  disabled={list.data.offset === 0}
                  className="rounded-md border border-border px-3 py-1.5 text-sm font-semibold disabled:opacity-50"
                >
                  Zurück
                </button>
                <button
                  type="button"
                  onClick={() => setListOffset((o) => o + RECIPIENT_PAGE_SIZE)}
                  disabled={list.data.offset + list.data.rows.length >= list.data.filtered}
                  className="rounded-md border border-border px-3 py-1.5 text-sm font-semibold disabled:opacity-50"
                >
                  Weiter
                </button>
              </div>
            ) : null}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">
            Der Empfängerkreis ist noch nicht festgeschrieben.
          </p>
        )}
      </section>
    </div>
  );
}

const ZUSTAND_CLASS: Record<RecipientViewState, string> = {
  zugestellt: "bg-brand-wahlen/25 text-foreground",
  ruecklaeufer: "bg-destructive/15 text-destructive",
  unterdrueckt: "bg-muted text-muted-foreground",
  abgemeldet: "bg-muted text-muted-foreground",
  unterwegs: "bg-primary/15 text-primary",
  fehlgeschlagen: "border border-destructive text-destructive",
  offen: "border border-border text-muted-foreground",
};

function ZustandBadge({ view }: { view: RecipientViewState }) {
  return (
    <span className={"rounded-md px-2 py-0.5 text-xs font-semibold " + ZUSTAND_CLASS[view]}>
      {RECIPIENT_VIEW_STATE_LABELS[view]}
    </span>
  );
}

function Kennzahl({
  label,
  value,
  strong = false,
}: {
  label: string;
  value: number;
  strong?: boolean;
}) {
  return (
    <div className="rounded-lg border border-border p-4">
      <div className={strong ? "text-3xl font-semibold text-primary" : "text-2xl font-semibold"}>
        {value}
      </div>
      <div className="mt-1 text-xs text-muted-foreground">{label}</div>
    </div>
  );
}
